import './Bundle.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionBottomSheet,
  AnimatedEmoji,
  Header,
  Placeholder,
  ScrollView,
} from '$uikit';
import { IcDelete28, IcEdit28, IcMore28, IcShare24 } from '$assets';
import { BundleProps } from '$core/Bundle/Bundle.interface';
import { useDispatch, useSelector } from 'react-redux';
import { bundlesActions, bundleSelector, bundlesFailedSelector } from '$store/bundles';
import {
  Currency,
  Linkify,
  Loader,
  MediaViewer,
  Slider,
  UnitLink,
  UnitName,
  UnitPhoto,
} from '$shared/components';
import { RootState } from '$store/rootReducer';
import { useCurrentUserId, useTranslator, useUserInfo } from '$hooks';
import { BundleAttachItem } from '$core/CreateBundle/BundleAttachItem/BundleAttachItem';
import { showModal } from '$navigation/helper';
import copyToClipboard from 'copy-text-to-clipboard';
import { toastActions } from '$store/toast';
import { ActionBottomSheetOption } from '$uikit/ActionBottomSheet/ActionBottomSheet.interface';
import { confirmSheet, rnbr, statReachGoal } from '$utils';
import { balancePayActions } from '$store/balancePay';
import { BalancePayAction } from '$store/balancePay/interface';
import { AttachType, CurrencyEnum } from '$store/models';
import { DiscountButton } from '$uikit/DiscountButton/DiscountButton';

enum actions {
  EDIT = 'edit',
  DELETE = 'delete',
  SHARE = 'share',
}

enum ViewMediaType {
  PREVIEWS = 'PREVIEWS',
  CONTENT = 'CONTENT',
}

export const Bundle: FC<BundleProps> = ({ id }) => {
  const [showActions, setShowActions] = useState(false);
  const dispatch = useDispatch();
  const [[viewMediaType, viewMediaIndex], setViewMedia] = useState<
    [ViewMediaType | null, number]
  >([null, 0]);
  const t = useTranslator();
  const currentUserId = useCurrentUserId();
  const bundle = useSelector((root: RootState) => bundleSelector(root, id));
  const failed = useSelector(bundlesFailedSelector);
  const user = useUserInfo(bundle?.userId || 0);
  const decryptKeys = useSelector((state: RootState) => state.bundles.decryptKeys);
  const decryptKey = id ? decryptKeys[id] : undefined;

  useEffect(() => {
    dispatch(bundlesActions.getBundle(id));
    statReachGoal('bundles_bundle_view', {
      bundleId: id,
    });
  }, [id]);

  const handleActionChange = useCallback(
    async (value: string) => {
      setShowActions(false);
      if (value === actions.EDIT) {
        showModal('edit_bundle', false, {}, { id });
      } else if (value === actions.DELETE) {
        if (await confirmSheet(t('bundles_bundle_remove_confirm'))) {
          dispatch(bundlesActions.deleteBundle(id));
        }
      } else if (value === actions.SHARE) {
        copyToClipboard(
          `${window.location.origin}/id${bundle.userId}?w=bundle${bundle.id}`,
        );
        dispatch(toastActions.setToastSuccess(t('copied')));
      }
    },
    [t, id, bundle],
  );

  const isMyBundle = bundle?.userId === currentUserId;
  const hasAccess = !!decryptKey || isMyBundle;
  const canBuy = bundle && !isMyBundle && !hasAccess;

  const [photoCount, videoCount] = useMemo(() => {
    let videoCount = 0;
    let photoCount = 0;
    bundle?.content?.forEach((a) => {
      if (a.type === AttachType.PHOTO) {
        photoCount++;
      } else if (a.type === AttachType.VIDEO) {
        videoCount++;
      }
    });
    return [photoCount, videoCount];
  }, [bundle]);

  const actionOptions = useMemo(() => {
    const options: ActionBottomSheetOption[] = [
      {
        icon: <IcShare24 />,
        label: t('bundles_bundle_action_share'),
        value: actions.SHARE,
      },
    ];

    if (isMyBundle && !bundle.calendarId) {
      options.push(
        {
          icon: <IcEdit28 />,
          label: t('bundles_bundle_action_edit'),
          value: actions.EDIT,
        },
        {
          icon: <IcDelete28 />,
          label: t('bundles_bundle_action_delete'),
          mode: 'alert',
          value: actions.DELETE,
        },
      );
    }

    return options;
  }, [t, isMyBundle]);

  const handleBuy = useCallback(() => {
    dispatch(
      balancePayActions.open({
        amount: bundle.price,
        action: BalancePayAction.BUNDLE,
        objectId: bundle.id,
      }),
    );
    statReachGoal('bundles_bundle_click_buy', {
      bundleId: bundle.id,
    });
  }, [bundle]);

  const contentDescription = useMemo(() => {
    // You will get 6 videos & 9 photos
    // 6 videos & 9 photos
    const content = [
      videoCount && t('videos_count', { count: videoCount }),
      photoCount && t('photos_count', { count: photoCount }),
    ]
      .filter(Boolean)
      .join(' & ');
    const result = hasAccess ? content : t('bundles_bundle_content_desc', { content });
    return result.charAt(0).toUpperCase() + result.slice(1);
  }, [t, hasAccess, videoCount, videoCount, bundle]);

  return (
    <>
      <Header
        showBackButton
        rightIcons={[
          {
            icon: <IcMore28 />,
            onClick: () => setShowActions(true),
          },
        ]}
      >
        {t('bundles_bundle_title')}
      </Header>
      <ScrollView
        footer={
          canBuy && (
            <DiscountButton
              price={bundle.price}
              priceWithoutDiscount={bundle.priceWithoutDiscount}
              currency={bundle.currency ?? CurrencyEnum.EUR}
              buyText="bundles_bundle_buy_button"
              saveText="bundles_bundle_buy_save"
              onClick={handleBuy}
            />
          )
        }
      >
        {failed ? (
          <Placeholder
            iconSize={120}
            icon={<AnimatedEmoji emoji="🗿" />}
            title={t('bundles_bundle_not_found_title')}
          />
        ) : bundle?.content ? (
          <div className="Bundle">
            <MediaViewer
              decryptKey={decryptKey}
              active={!!viewMediaType}
              startFrom={viewMediaIndex}
              attachments={
                viewMediaType === ViewMediaType.CONTENT ? bundle.content : bundle.previews
              }
              onClose={() => setViewMedia([null, 0])}
            />
            <div className="Bundle__title">{bundle.name}</div>
            <Linkify className="Bundle__description">{rnbr(bundle.description)}</Linkify>
            <UnitLink className="Bundle__author" unit={user}>
              <UnitPhoto
                nftIndicator={false}
                onlineIndicator={false}
                size={'extra-small'}
                unit={user}
              />
              <UnitName unit={user} />
            </UnitLink>
            <div className="Bundle__preview">
              <div className="Bundle__preview__title">
                {t('bundles_bundle_preview_title')}
              </div>
              <Slider>
                {bundle.previews.map((attach, index) => (
                  <BundleAttachItem
                    onClick={() => setViewMedia([ViewMediaType.PREVIEWS, index])}
                    decryptKey={decryptKey}
                    key={index}
                    attach={attach}
                  />
                ))}
              </Slider>
            </div>
            <div className="Bundle__content">
              <div className="Bundle__content__title">
                {t('bundles_bundle_content_title')}
                <div className="Bundle__content__prices">
                  {bundle.priceWithoutDiscount > bundle.price && (
                    <Currency
                      className="Bundle__content__price--old"
                      amount={bundle.priceWithoutDiscount}
                      currency={bundle.currency}
                    />
                  )}
                  <Currency
                    className="Bundle__content__price--current"
                    amount={bundle.price}
                    currency={bundle.currency}
                  />
                </div>
              </div>
              <div className="Bundle__content__subTitle">{contentDescription}</div>
              <div className="Bundle__content__grid">
                {bundle.content.map((attach, index) => (
                  <BundleAttachItem
                    onClick={
                      hasAccess
                        ? () => setViewMedia([ViewMediaType.CONTENT, index])
                        : undefined
                    }
                    hasAccess={hasAccess}
                    decryptKey={decryptKey}
                    key={index}
                    attach={attach}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <Loader isWrapped />
        )}
      </ScrollView>
      <ActionBottomSheet
        title={t('bundles_bundle_actions_title')}
        isOpened={showActions}
        options={actionOptions}
        onClose={() => setShowActions(false)}
        onChange={handleActionChange}
      />
    </>
  );
};
